import { useState } from 'react';
import { Plus, Trash2, Lock, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NodeParam, InputValue } from '@/types/node-types';
import { McpServerModal } from './mcp-server-modal';

export type MCPServerConfig = {
  url: string;
  label: string;
  description?: string;
  authentication: {
    type: 'none' | 'api_key' | 'custom_headers';
    api_key?: string;
    header_name?: string;
    headers?: Record<string, string>;
  };
};

interface MCPServersInputProps {
  field: NodeParam;
  fieldId: string;
  isFieldConnected: boolean;
  isDisabled?: boolean;
  handleFieldChange: (key: string, value: InputValue) => void;
}

export const MCPServersInput: React.FC<MCPServersInputProps> = ({
  field,
  fieldId,
  isFieldConnected,
  isDisabled = false,
  handleFieldChange,
}) => {
  const [isMcpServerModalOpen, setIsMcpServerModalOpen] = useState(false);
  const [editingServerIndex, setEditingServerIndex] = useState<number | null>(
    null,
  );

  const getMcpServers = (): MCPServerConfig[] => {
    const value = field.value;
    if (!value) return [];

    // Handle object with mcp_servers property
    if (typeof value === 'object' && value !== null && 'mcp_servers' in value) {
      const servers = (value as { mcp_servers: unknown }).mcp_servers;
      if (Array.isArray(servers)) {
        const result: MCPServerConfig[] = [];
        for (const s of servers) {
          if (
            typeof s === 'object' &&
            s !== null &&
            'url' in s &&
            'label' in s &&
            'authentication' in s
          ) {
            result.push(s as MCPServerConfig);
          }
        }
        return result;
      }
    }

    // Handle direct array (for backwards compatibility)
    if (Array.isArray(value)) {
      const result: MCPServerConfig[] = [];
      for (const s of value) {
        if (
          typeof s === 'object' &&
          s !== null &&
          'url' in s &&
          'label' in s &&
          'authentication' in s
        ) {
          result.push(s as MCPServerConfig);
        }
      }
      return result;
    }

    return [];
  };

  const handleAddMcpServer = () => {
    setEditingServerIndex(null);
    setIsMcpServerModalOpen(true);
  };

  const handleEditMcpServer = (index: number) => {
    setEditingServerIndex(index);
    setIsMcpServerModalOpen(true);
  };

  const handleMcpServerSave = (server: MCPServerConfig) => {
    const currentServers = getMcpServers();

    if (editingServerIndex !== null) {
      // Edit mode: replace the server at the index
      const updatedServers = currentServers.map((s, i) =>
        i === editingServerIndex ? server : s,
      );
      handleFieldChange(fieldId, {
        mcp_servers: updatedServers,
      } as unknown as InputValue);
    } else {
      // Add mode: append the new server
      const updatedServers = [...currentServers, server];
      handleFieldChange(fieldId, {
        mcp_servers: updatedServers,
      } as unknown as InputValue);
    }
  };

  const handleModalClose = () => {
    setIsMcpServerModalOpen(false);
    setEditingServerIndex(null);
  };

  const handleRemoveMcpServer = (index: number) => {
    const currentServers = getMcpServers();
    const updatedServers = currentServers.filter((_, i) => i !== index);
    handleFieldChange(fieldId, {
      mcp_servers: updatedServers,
    } as unknown as InputValue);
  };

  const servers = getMcpServers();
  const hasServers = servers.length > 0;

  return (
    <div className='relative'>
      <div className='space-y-2'>
        {hasServers
          ? servers.map((server, index) => (
              <div
                key={index}
                className='flex items-start justify-between p-3 border rounded-md bg-muted/30'
              >
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 mb-1'>
                    <span className='font-medium text-sm'>{server.label}</span>
                    <span className='text-xs text-muted-foreground'>
                      ({server.authentication.type})
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground truncate'>
                    {server.url}
                  </p>
                  {server.description && (
                    <p className='text-xs text-muted-foreground mt-1'>
                      {server.description}
                    </p>
                  )}
                </div>
                {!isFieldConnected && !isDisabled && (
                  <div className='flex gap-1'>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() => handleEditMcpServer(index)}
                      className='h-6 w-6'
                      aria-label={`Edit ${server.label} MCP server`}
                    >
                      <Pencil className='h-4 w-4' />
                    </Button>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() => handleRemoveMcpServer(index)}
                      className='h-6 w-6 text-destructive hover:text-destructive'
                      aria-label={`Remove ${server.label} MCP server`}
                    >
                      <Trash2 className='h-4 w-4' />
                    </Button>
                  </div>
                )}
              </div>
            ))
          : !isFieldConnected &&
            !isDisabled && (
              <div className='flex items-center justify-center h-10 px-3 py-2 text-sm text-muted-foreground border border-input rounded-md bg-muted/50'>
                <span>No MCP servers configured</span>
              </div>
            )}
        {!isFieldConnected && !isDisabled && (
          <Button
            variant='outline'
            onClick={handleAddMcpServer}
            className='w-full flex items-center gap-2'
            disabled={isFieldConnected}
          >
            <Plus className='h-4 w-4' />
            Add MCP Server
          </Button>
        )}
        {(isFieldConnected || isDisabled) && (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Lock className='h-4 w-4' />
            <span>
              {isFieldConnected ? 'Field is connected' : 'Field is locked'}
            </span>
          </div>
        )}
      </div>

      <McpServerModal
        isOpen={isMcpServerModalOpen}
        onClose={handleModalClose}
        onSave={handleMcpServerSave}
        initialData={
          editingServerIndex !== null ? servers[editingServerIndex] : undefined
        }
      />
    </div>
  );
};
